import { useRef } from 'react';
import type { CSSProperties, PointerEvent, ReactNode } from 'react';
import type { Card, PublicPlayer, Role } from '../game/types';
import { cardAccent, cardRank, cardSuit, roleColor } from '../game/cardMeta';
import { cardArt, characterArt } from '../game/art';
import { cardBackCss, savedCardBack } from '../game/shop';
import { CardIcon, RoleIcon } from './Icon';
import { AvatarFrame } from './Frames';
import { useT } from '../i18n';

export function Backdrop() {
  return <div className="backdrop" aria-hidden />;
}

export function Spinner() {
  return <div className="spinner" role="status" aria-label="loading" />;
}

/** First-name + last-name initials: "Artem Kutukov" → "AK", "Runner" → "RU". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function IntegrityBar({ value, max }: { value: number; max: number }) {
  const pips = Math.max(max, value);
  return (
    <div className="integrity">
      {Array.from({ length: pips }, (_, i) => (
        <span key={i} className={'pip ' + (i < value ? (value <= 1 ? 'low' : 'full') : '')} />
      ))}
    </div>
  );
}

export function Avatar({ player }: { player: PublicPlayer }) {
  const color =
    player.roleRevealed && player.revealedRole ? roleColor[player.revealedRole] : 'var(--dim)';
  const portrait = characterArt(player.characterId);
  return (
    <div className={'avatar' + (player.frame ? ' frame-' + player.frame : '')} style={{ color }}>
      {portrait ? (
        <img className="avatar-img" src={portrait} alt="" />
      ) : player.photoURL ? (
        <img className="avatar-img" src={player.photoURL} alt="" referrerPolicy="no-referrer" />
      ) : (
        initials(player.displayName)
      )}
      <AvatarFrame frame={player.frame} />
    </div>
  );
}

/** Rank + suit corner index, like a classic playing card. */
function CornerIndex({ card, className }: { card: Card; className: string }) {
  const suit = cardSuit[card.kind];
  return (
    <span className={'card-idx ' + className} style={{ color: suit.color }}>
      <span className="idx-rank">{cardRank(card.id)}</span>
      <span className="idx-suit">{suit.glyph}</span>
    </span>
  );
}

export function CardFace({
  card,
  selected,
  disabled,
  onClick,
  onDisabledClick,
  onInspect,
  style,
}: {
  card: Card;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /** Tapping a disabled card explains WHY it can't be played right now. */
  onDisabledClick?: () => void;
  /** Long-press (or right-click) to inspect the card's full art, even when it
   * can't be played. Absent = no inspect gesture (e.g. picker cards). */
  onInspect?: () => void;
  style?: CSSProperties;
}) {
  const t = useT();
  const name = t.card.name[card.kind];
  const desc = t.card.short[card.kind];
  const nameChars = Array.from(name).length;
  const titleFontSize = Math.max(9.5, Math.min(15, 118 / Math.max(nameChars, 8)));

  // Long-press → inspect. A held press fires onInspect and suppresses the click
  // that follows (so inspecting never also plays the card). Movement cancels it,
  // so a drag/scroll doesn't count as a press.
  const lpTimer = useRef<number | null>(null);
  const lpFired = useRef(false);
  const lpStart = useRef<{ x: number; y: number } | null>(null);
  const cancelLP = () => {
    if (lpTimer.current !== null) {
      clearTimeout(lpTimer.current);
      lpTimer.current = null;
    }
    lpStart.current = null;
  };
  const startLP = (e: PointerEvent) => {
    if (!onInspect) return;
    lpFired.current = false;
    lpStart.current = { x: e.clientX, y: e.clientY };
    lpTimer.current = window.setTimeout(() => {
      lpFired.current = true;
      cancelLP();
      onInspect();
    }, 420);
  };
  const moveLP = (e: PointerEvent) => {
    if (!lpStart.current) return;
    if (Math.hypot(e.clientX - lpStart.current.x, e.clientY - lpStart.current.y) > 10) cancelLP();
  };
  const handleClick = () => {
    if (lpFired.current) {
      lpFired.current = false;
      return; // the long-press already opened the inspector
    }
    (disabled ? onDisabledClick : onClick)?.();
  };

  return (
    <div
      className={
        'card ' +
        (cardAccent[card.kind] ?? 'a-cyan') +
        (selected ? ' selected' : '') +
        (disabled ? ' disabled' : '')
      }
      onClick={handleClick}
      onPointerDown={startLP}
      onPointerMove={moveLP}
      onPointerUp={cancelLP}
      onPointerLeave={cancelLP}
      onPointerCancel={cancelLP}
      onContextMenu={
        onInspect
          ? (e) => {
              e.preventDefault();
              lpFired.current = true;
              onInspect();
            }
          : undefined
      }
      role="button"
      tabIndex={disabled ? -1 : 0}
      style={style}
    >
      <CornerIndex card={card} className="tl" />
      <CornerIndex card={card} className="br" />
      <div className="card-art" style={{ backgroundImage: `url(${cardArt(card.kind)})` }} />
      <div className="card-type-mark"><CardIcon kind={card.kind} size={15} /></div>
      <div className="card-copy">
        <div className="card-name" style={{ fontSize: `${titleFontSize}px` }}>{name}</div>
        <div className="card-desc">{desc}</div>
      </div>
    </div>
  );
}

/** Compact face-up card for the discard pile — rank/suit corner, icon + name. */
export function MiniCard({ card }: { card: Card }) {
  const t = useT();
  return (
    <div className={'mini-card ' + (cardAccent[card.kind] ?? 'a-cyan')} title={t.card.name[card.kind]}>
      <div className="mini-art" style={{ backgroundImage: `url(${cardArt(card.kind)})` }} />
      <CornerIndex card={card} className="tl" />
      <div className="mini-name">{t.card.name[card.kind]}</div>
    </div>
  );
}

/** Face-down card back — the equipped shop skin, else the stock neon art. */
export function CardBack({ className }: { className?: string }) {
  return (
    <div
      className={'card-back' + (className ? ' ' + className : '')}
      style={cardBackCss(savedCardBack())}
    />
  );
}

export function RoleBadge({ role }: { role: Role }) {
  const t = useT();
  return (
    <span className="tag role-tag-inline" style={{ color: roleColor[role] }}>
      <RoleIcon role={role} size={12} /> {t.role.name[role]}
    </span>
  );
}

export function Modal({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="panel modal" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
